import { useEffect, useRef } from 'react';

import { TranslatedComponent, translation } from '@hh.ru/front-static-app';
import {
    Action,
    BottomSheet,
    BottomSheetFooter,
    Button,
    Cell,
    CellText,
    CheckableCard,
    NavigationBar,
    Radio,
    SearchInput,
    useBreakpoint,
    useDebounce,
    VSpacing,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import Drawer from 'src/components/Drawer';
import { TOP_AREAS } from 'src/fixtures/areas';
import useSafeState from 'src/hooks/useSafeState';
import { AreaLeavesDto } from 'src/types/area';
import getDataQa from 'src/utils/getDataQa';
import findCity from 'src/widgets/GeoLocation/api/findCity';
import GeoLocationSearchEmptyState from 'src/widgets/GeoLocation/components/GeoLocationSearchEmptyState';

const TrlKeys = {
    title: 'career-platform.widgets.geo-location.search-dialog.title',
    description: 'career-platform.widgets.geo-location.search-dialog.description',
    selectButton: 'career-platform.widgets.geo-location.search-dialog.select-button',
    input: {
        placeholder: 'career-platform.widgets.geo-location.search-dialog.input.placeholder',
    },
};

interface Props {
    visible: boolean;
    selectedCity: AreaLeavesDto;
    onSelectCity: (city: AreaLeavesDto) => void;
    onClose: () => void;
}

const DATA_QA_BLOCK = 'change-geo-location';

const GeoLocationSearchDialog: TranslatedComponent<Props> = ({
    visible,
    selectedCity,
    onSelectCity,
    onClose,
    trls,
}) => {
    const { isMobile, isXS } = useBreakpoint();

    const [searchValue, setSearchValue] = useSafeState('');
    const [cities, setCities] = useSafeState<AreaLeavesDto[]>(TOP_AREAS);
    const [localSelectedCity, setLocalSelectedCity] = useSafeState<AreaLeavesDto>(selectedCity);

    const searchRef = useRef<HTMLInputElement | null>(null);
    const abortController = useRef<AbortController | null>(null);

    const handleConfirm = () => {
        onSelectCity(localSelectedCity);
        onClose();
    };

    const fetchDataRef = useRef(async (searchQuery: string) => {
        try {
            abortController.current = new AbortController();
            const cities = await findCity(searchQuery, { signal: abortController.current?.signal });
            setCities(cities);
        } catch {} // eslint-disable-line no-empty
    });

    const debouncedFetchData = useDebounce(fetchDataRef.current, 200);

    const abortFetch = () => {
        debouncedFetchData.cancel();
        abortController.current?.abort();
    };

    useEffect(() => {
        abortFetch();
        if (searchValue.length >= 2) {
            debouncedFetchData(searchValue);
        } else {
            setCities(TOP_AREAS);
        }
    }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setSearchValue('');
    }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return () => abortFetch();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!visible || isXS) {
            return () => undefined;
        }
        const id = setTimeout(() => {
            searchRef.current?.focus();
        }, 200);
        return () => {
            clearTimeout(id);
        };
    }, [isXS, visible]);

    const SearchComponent = (
        <SearchInput
            ref={searchRef}
            placeholder={trls[TrlKeys.input.placeholder]}
            value={searchValue}
            clearable
            onChange={setSearchValue}
            tabIndex={1}
            data-qa={getDataQa(DATA_QA_BLOCK, 'search')}
        />
    );

    const Content = !cities.length ? (
        <GeoLocationSearchEmptyState />
    ) : (
        <VSpacingContainer default={12}>
            {cities.map((city) => {
                const isChecked = city.id === localSelectedCity.id;
                const RadioComponent = <Radio checked={isChecked} readOnly tabIndex={-1} />;
                return (
                    <CheckableCard
                        key={city.id}
                        borderRadius={16}
                        padding={16}
                        type="checkbox"
                        readOnly
                        checked={isChecked}
                        onClick={() => setLocalSelectedCity(city)}
                        tabIndex={2}
                        data-qa={getDataQa(DATA_QA_BLOCK, 'find-city', city.id, isChecked ? 'selected' : undefined)}
                    >
                        <Cell
                            left={!isMobile ? RadioComponent : undefined}
                            right={isMobile ? RadioComponent : undefined}
                        >
                            <CellText type="title">{city.text}</CellText>
                        </Cell>
                    </CheckableCard>
                );
            })}
        </VSpacingContainer>
    );

    const ConfirmButton = cities.length ? (
        <Button
            size="medium"
            mode="primary"
            style="accent"
            stretched
            onClick={handleConfirm}
            tabIndex={3}
            disabled={cities.every(({ id }) => id !== localSelectedCity.id)}
            data-qa={getDataQa(DATA_QA_BLOCK, 'confirm')}
        >
            {trls[TrlKeys.selectButton]}
        </Button>
    ) : null;

    if (!isMobile) {
        return (
            <Drawer
                title={trls[TrlKeys.title]}
                description={trls[TrlKeys.description]}
                visible={visible}
                onClose={onClose}
                options={SearchComponent}
                buttons={ConfirmButton}
                dataQaContainer={getDataQa(DATA_QA_BLOCK)}
                dataQaCloseButton={getDataQa(DATA_QA_BLOCK, 'close')}
                dataQaBackdrop={getDataQa(DATA_QA_BLOCK, 'backdrop')}
            >
                {Content}
            </Drawer>
        );
    }

    return (
        <BottomSheet
            visible={visible}
            onClose={onClose}
            header={
                <NavigationBar
                    size="standard"
                    title={trls[TrlKeys.title]}
                    right={<Action icon={CrossOutlinedSize24} onClick={onClose}></Action>}
                    options={SearchComponent}
                />
            }
            footer={<BottomSheetFooter>{ConfirmButton}</BottomSheetFooter>}
        >
            {Content}
            <VSpacing default={24} />
        </BottomSheet>
    );
};

export default translation(GeoLocationSearchDialog);
